import {
  type AllReportStatus,
  allReportStatus,
  type KeyInfoItem,
  REPORT_TYPE_KINDS,
  type ReportLocation,
  type ReportType,
  reportKeyInfo,
  reportLocation,
  reportTypeOf,
  type VoyageReportKind,
} from '@felix/domain';
import type { SortDirection } from '../purchasing/requisitions.js';
import { UNLINKED } from './screen-load.js';
import type { Voyage, VoyageReport } from './types.js';
import { assignedVoyageIdOf } from './voyage-record.js';

/**
 * All Reports ekranının saf yardımcıları (ADR 0082; ortağın `sefer-6.jpeg`): satırlar, süzgeçler,
 * sıralama, form bağlantıları ve CSV. Rapor türü, konum ve Key Information domain'den türetilir.
 */

/** Menüdeki rapor öğelerinin yolu (`/voyage/reports/<yol>`); "other" menüde yok. */
export const REPORT_TYPE_PATHS: Readonly<Record<Exclude<ReportType, 'other'>, string>> = {
  arrival: 'arrival',
  port_daily: 'daily-port',
  departure: 'departure',
  anchoring: 'anchoring',
  anchor_aweigh: 'anchor-aweigh',
  drifting: 'drifting',
  drift_departure: 'drift-departure',
  canal_strait: 'canal-strait',
  noon: 'noon',
};

export const ALL_REPORTS_PATH = '/voyage/reports';

/** Yolun tür süzgeci: `/voyage/reports/noon` → `noon`; All Reports'ta `null`. */
export function reportTypeFromPath(pathname: string): ReportType | null {
  const segment = pathname.startsWith(`${ALL_REPORTS_PATH}/`)
    ? pathname.slice(ALL_REPORTS_PATH.length + 1).split('/')[0]
    : null;
  const found = Object.entries(REPORT_TYPE_PATHS).find(([, path]) => path === segment);
  return found ? (found[0] as ReportType) : null;
}

export interface AllReportRow {
  report: VoyageReport;
  type: ReportType;
  /** Raporun seferi; seferi başlatan sefersiz kalkışta bağlayan sefer (ADR 0058 §3). */
  voyage: Voyage | null;
  location: ReportLocation;
  keyInfo: KeyInfoItem[];
  status: AllReportStatus;
}

/** Raporlar ve seferler → satırlar. */
export function allReportRows(
  reports: readonly VoyageReport[],
  voyages: readonly Voyage[],
): AllReportRow[] {
  const byId = new Map(voyages.map((voyage) => [voyage.id, voyage]));
  return reports.map((report) => ({
    report,
    type: reportTypeOf(report.kind),
    // Kendi seferi ya da bağlandığı sefer (ADR 0083 §2)
    voyage: byId.get(assignedVoyageIdOf(report, voyages) ?? '') ?? null,
    location: reportLocation(report),
    keyInfo: reportKeyInfo(report),
    status: allReportStatus(report),
  }));
}

/** Seçili sefer: kimlik ya da sefersizler (`unassigned`). */
export const UNASSIGNED = 'unassigned';

export interface AllReportsFilter {
  /** UTC günü `YYYY-MM-DD` (tablo saatleri UTC). */
  from: string | null;
  to: string | null;
  type: ReportType | null;
  voyage: string | null;
  /** `locationKey` değeri. */
  location: string | null;
  status: AllReportStatus | null;
}

export const NO_ALL_REPORTS_FILTER: AllReportsFilter = {
  from: null,
  to: null,
  type: null,
  voyage: null,
  location: null,
  status: null,
};

/** Konumun süzgeç anahtarı: denizde, demirde ya da adıyla liman, alan, geçiş. */
export function locationKey(location: ReportLocation): string {
  switch (location.type) {
    case 'port':
    case 'area':
    case 'passage':
      return `${location.type}:${location.name.toLocaleLowerCase('en')}`;
    default:
      return location.type;
  }
}

/** Süzgecin konum seçenekleri: önce denizde ve demirde, sonra adlar alfabetik; bilinmeyen yok. */
export function locationOptions(
  rows: readonly AllReportRow[],
): { value: string; location: ReportLocation }[] {
  const seen = new Map<string, ReportLocation>();
  for (const row of rows) {
    if (row.location.type === 'unknown') continue;
    const key = locationKey(row.location);
    if (!seen.has(key)) seen.set(key, row.location);
  }
  const rank = (location: ReportLocation) =>
    location.type === 'sea' ? 0 : location.type === 'anchorage' ? 1 : 2;
  const name = (location: ReportLocation) => ('name' in location ? location.name : '');
  return [...seen.entries()]
    .map(([value, location]) => ({ value, location }))
    .sort(
      (a, b) =>
        rank(a.location) - rank(b.location) ||
        name(a.location).localeCompare(name(b.location), 'en'),
    );
}

const utcDay = (iso: string) => new Date(iso).toISOString().slice(0, 10);

export function filterAllReports(
  rows: readonly AllReportRow[],
  filter: AllReportsFilter,
): AllReportRow[] {
  return rows.filter((row) => {
    const day = utcDay(row.report.reportedAt);
    if (filter.from && day < filter.from) return false;
    if (filter.to && day > filter.to) return false;
    if (filter.type && row.type !== filter.type) return false;
    if (
      filter.voyage === UNASSIGNED
        ? row.voyage !== null
        : filter.voyage && row.voyage?.id !== filter.voyage
    )
      return false;
    if (filter.location && locationKey(row.location) !== filter.location) return false;
    if (filter.status && row.status !== filter.status) return false;
    return true;
  });
}

export type AllReportsColumn = 'date' | 'type' | 'location' | 'voyage' | 'status';

/**
 * Sıralama: varsayılan en yeni üstte (sefer-6). Diğer sütunlarda eşitlikte yine zamana göre;
 * metinler `label` ile (ekrandaki metin) karşılaştırılır.
 */
export function sortAllReports(
  rows: readonly AllReportRow[],
  sort: { key: AllReportsColumn; dir: SortDirection },
  label: (row: AllReportRow, key: Exclude<AllReportsColumn, 'date'>) => string,
): AllReportRow[] {
  const sign = sort.dir === 'asc' ? 1 : -1;
  const time = (row: AllReportRow) => Date.parse(row.report.reportedAt);
  return [...rows].sort((a, b) => {
    if (sort.key !== 'date') {
      const order = label(a, sort.key).localeCompare(label(b, sort.key), 'en');
      if (order !== 0) return order * sign;
    }
    return (
      (time(a) - time(b)) * (sort.key === 'date' ? sign : -1) ||
      a.report.id.localeCompare(b.report.id)
    );
  });
}

/**
 * Raporun formu (View / Edit): Noon kendi ekranında, diğerleri Leg / Event'te (yolları menüde
 * görünmez ama yaşar; ADR 0082). Sefersiz raporlar "Without voyage" görünümünde.
 */
export function reportFormPath(row: AllReportRow, vesselId: string | null): string {
  const query = new URLSearchParams();
  if (vesselId) query.set('vessel', vesselId);
  query.set('voyage', row.voyage?.id ?? UNLINKED);
  query.set('report', row.report.id);
  const base = row.report.kind === 'noon' ? '/voyage/noon-report' : '/voyage/legs';
  return `${base}?${query.toString()}`;
}

/** Seferin olay günlüğü (Leg / Event) ya da sefersiz kalkışlar. */
export function voyageLogPath(row: AllReportRow, vesselId: string | null): string {
  const query = new URLSearchParams();
  if (vesselId) query.set('vessel', vesselId);
  query.set('voyage', row.voyage?.id ?? UNLINKED);
  return `/voyage/legs?${query.toString()}`;
}

/**
 * Quick Create Report (ADR 0082 Ek §4, ADR 0083 §6; sefer-6): tür ve sefer bağı. "No Voyage
 * Assigned" her türde sefersiz formu açar; "Link to Existing Voyage" seçilen seferin formunu;
 * "Create New Voyage" Create New Voyage'ı, sefer açılınca formu yeni seferle açmak üzere.
 */
export function quickCreatePath(
  kind: VoyageReportKind,
  association: 'none' | 'link' | 'new',
  voyageId: string | null,
): string {
  if (association === 'new') return `/voyage/new?then=${kind}`;
  const voyage = association === 'none' ? UNLINKED : (voyageId ?? '');
  return kind === 'noon'
    ? `/voyage/noon-report?voyage=${voyage}`
    : `/voyage/legs?voyage=${voyage}&kind=${kind}`;
}

/** "Add New Report": mevcut rapor akışları (yeni form yok); Noon kendi ekranında. */
export function newReportPath(kind: VoyageReportKind): string {
  return kind === 'noon' ? '/voyage/noon-report' : `/voyage/legs?kind=${kind}`;
}

/** "Add New Report" menüsünün türleri: süzgeçteki tür ya da hepsi, menü sırasıyla. */
export function newReportKinds(
  type: ReportType | null,
): { type: ReportType; kinds: readonly VoyageReportKind[] }[] {
  const types = type ? [type] : (Object.keys(REPORT_TYPE_KINDS) as ReportType[]);
  return types.map((t) => ({ type: t, kinds: REPORT_TYPE_KINDS[t] }));
}

/**
 * CSV hücresi: virgül, tırnak ve satır sonu tırnaklanır; formül gibi başlayan metin (=, +, @, -)
 * hesap tablosunda çalışmasın diye kesme işaretiyle başlar (CSV injection).
 */
export function csvCell(value: string): string {
  const safe = /^[=+@\t\r]|^-(?!\d)/.test(value) ? `'${value}` : value;
  return /[",\n\r]/.test(safe) ? `"${safe.replaceAll('"', '""')}"` : safe;
}

export function toCsv(head: readonly string[], rows: readonly (readonly string[])[]): string {
  return [head, ...rows].map((cells) => cells.map(csvCell).join(',')).join('\r\n');
}
